﻿import { useEffect, type ChangeEvent } from "react";
import { useState } from "react";
import { Typography, TextField, Grid, Autocomplete } from "@mui/material";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/typedDispatch"
import {
  fetchCustomerCodes,
  selectEditCatalogData,
  selectIsEditMode,
  setCatalogDataField,
  selectAddEditConnectionInfo,
  selectAvailableCustomers,
  type ICustomerArtifactData,
  type IAddCatalogPopup,
  type ICatalog,
} from "@/features/connections/manageCatalogDataSlice";
import { defaultCode, blankCode } from "@/features/connections/integrationSlice";
import { AddEditCatalogPopup } from "./CatalogPopup";
import { CatalogueTypeNone } from "@/utils/utils";
import _ from "lodash";

interface IValidateForm {
  isValid: boolean;
  message?: string;
}

const AddShipToPopup = ({ onClose, onValidate, onComplete }: IAddCatalogPopup) => {
  const dispatch = useAppDispatch();
  const dataValues = useSelector(selectEditCatalogData);
  const isEdit = useSelector(selectIsEditMode);
  const connectionInfo = useSelector(selectAddEditConnectionInfo);
  const [selectedCustomer, setCustomer] = useState<ICustomerArtifactData>({
    ArtifactDictionaryDataId: dataValues.CustomerId,
    Reference: dataValues.Customer?.CustomerCode ?? CatalogueTypeNone,
    ArtifactData: JSON.stringify(dataValues.Customer),
  });
  const customerCodes = useSelector(selectAvailableCustomers);
  const [customerCodeSearchValue, setCustomerCodeSearchValue] = useState("");

  const [options, setOptions] = useState<ICustomerArtifactData[]>([]);

  useEffect(() => {
    const list = Array.isArray(customerCodes) ? customerCodes : [];
    setOptions([{ Reference: "None", CustomerName: "" }, ...list]);
  }, [customerCodes])

  useEffect(() => {
    if (connectionInfo.connectionId !== -1) {
      dispatch(
        fetchCustomerCodes({
          selectedConnectionId: connectionInfo.connectionId
        }),
      );
    }
  }, [connectionInfo, dispatch]);

  const fetchCustomerCodeWrapper = (
    selectedConnectionId: number,
    searchString: string = ""
  ) => {
    setOptions([]);
    dispatch(
      fetchCustomerCodes({
        selectedConnectionId,
        searchString
      }),
    );
  }
  const debouncedFetchCustomerCodes = _.debounce(fetchCustomerCodeWrapper, 500)

  useEffect(() => {
    if (
        !!customerCodeSearchValue 
        && customerCodeSearchValue.length >= 2
        && customerCodeSearchValue !== CatalogueTypeNone
        && connectionInfo.connectionId !== -1
      ) {
      debouncedFetchCustomerCodes(connectionInfo.connectionId, customerCodeSearchValue)
    } else if (
      (
        !!customerCodeSearchValue 
        && customerCodeSearchValue.length < 2
      ) || customerCodeSearchValue === CatalogueTypeNone
    ) {
      debouncedFetchCustomerCodes(connectionInfo.connectionId)
    }
  }, [connectionInfo, customerCodeSearchValue, debouncedFetchCustomerCodes, dispatch])

  const updateCustomerInfo = async (dataValues: ICatalog) => {
    await setCustomer({
      ArtifactDictionaryDataId: dataValues.CustomerId,
      Reference: dataValues.Customer?.CustomerCode ?? CatalogueTypeNone,
      ArtifactData: JSON.stringify(dataValues.Customer),
    });
  }

  /* this does not need to be inside a useEffect, it is causing extra re-renders:

      Effects are intended to synchronize state between React and external systems such as manually updating the DOM, state management libraries, or other platform APIs. In general, the body of an effect should do one or both of the following:
    * Update external systems with the latest state from React.
    * Subscribe for updates from some external system, calling setState in a callback function when external state changes.

    Calling setState synchronously within an effect body causes cascading renders that can hurt performance, and is not recommended. (https://react.dev/learn/you-might-not-need-an-effect).

    C:\Users\ATerekhovskyy\source\repos\shep.io\LeapFrogBeta\ClientApp\src\components\Maintenance\AddCatalogs\AddItemUOMPopup.tsx:75:7
      73 |   useEffect(() => {
      74 |     if (selectedItem.ArtifactDictionaryDataId !== dataValues.ItemId) {
    > 75 |       setItem({
        |       ^^^^^^^ Avoid calling setState() directly within an effect
      76 |         ArtifactDictionaryDataId: dataValues.ItemId,
      77 |         Code: dataValues.ItemCode ?? "None",
      78 |         Description: "",  react-hooks/set-state-in-effect
  */
  if (selectedCustomer.ArtifactDictionaryDataId !== dataValues.CustomerId) {
    updateCustomerInfo(dataValues);
  }
  const handleCustomerChange = (newCustomer: ICustomerArtifactData | null) => {
    if (
      newCustomer &&
      newCustomer.ArtifactData &&
      newCustomer.Reference !== CatalogueTypeNone
    ) {
      dispatch(
        setCatalogDataField({
          name: "Customer",
          value: JSON.parse(newCustomer.ArtifactData),
        }),
      );
    } else {
      dispatch(setCatalogDataField({ name: "Customer", value: null }));
    }
    if (newCustomer && newCustomer.Reference !== CatalogueTypeNone) {
      dispatch(
        setCatalogDataField({
          name: "CustomerId",
          value: newCustomer.ArtifactDictionaryDataId,
        }),
      );
    } else {
      dispatch(setCatalogDataField({ name: "CustomerId", value: null }));
    }
  };
  const validateForm = (): IValidateForm => {
    if (onValidate) {
      const result = onValidate(dataValues);
      if (!result.isValid) return result;
    }
    if (dataValues.ShipToCode === defaultCode) {
      return {
        isValid: false,
        message: `Ship To Code cannot be saved as ${defaultCode}`,
      };
    }
    if (dataValues.ShipToCode === blankCode) {
      return {
        isValid: false,
        message: `Ship To Code cannot be saved as ${blankCode}`,
      };
    }
    if (dataValues.ShipToCode?.includes(",")) {
      return {
        isValid: false,
        message: `The Ship To Code cannot include a ','`,
      };
    }
    if (selectedCustomer == null) {
      return {
        isValid: false,
        message: "Please select an option from the Customer menu.",
      };
    }
    return { isValid: true };
  };

  const handleClose = () => {
    onClose();
    setCustomer({
      ArtifactDictionaryDataId: null,
      Reference: CatalogueTypeNone,
      ArtifactData: ""
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    dispatch(setCatalogDataField({ name, value }));
  };

  return (
    <AddEditCatalogPopup
      onClose={handleClose}
      onValidate={validateForm}
      onComplete={onComplete}
    >
      <Grid item xs={4}>
        <Typography>
          Customer{" "}
          <Typography
            component="span"
            sx={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
          >
            *
          </Typography>
        </Typography>
        <Autocomplete
          disabled={isEdit}
          options={options}
          getOptionLabel={(option) =>
            option.Reference === CatalogueTypeNone
              ? CatalogueTypeNone
              : `${option.Reference ?? ""} ${option.CustomerName ?? ""}`
          }
          getOptionKey={(option) => option.Reference}
          isOptionEqualToValue={(option, value) =>
            option.Reference === value.Reference
          }
          value={selectedCustomer || null}
          onChange={(_e, newValue) => {
            handleCustomerChange(newValue || null);
          }}
          renderInput={(params) => (
            <TextField
              sx={{ marginTop: 1, marginBottom: 2, maxWidth: 250 }}
              {...params}
              autoComplete="off"
            />
          )}
          inputValue={customerCodeSearchValue}
          onInputChange={(_e, newValue) => {
            setCustomerCodeSearchValue(newValue)
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Code </Typography>
        <TextField
          disabled={isEdit}
          variant="outlined"
          name="ShipToCode"
          value={dataValues?.ShipToCode || ""}
          onChange={handleChange}
          sx={{
            width: 250,
            marginTop: 1,
            marginBottom: 2,
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Name</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="ShipToName"
            value={dataValues?.ShipToName || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Address 1</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Address1"
            value={dataValues?.Address1 || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Address 2</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Address2"
            value={dataValues?.Address2 || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Address 3</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Address3"
            value={dataValues?.Address3 || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Address 4</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Address4"
            value={dataValues?.Address4 || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To City</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="City"
            value={dataValues?.City || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To State</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="State"
            value={dataValues?.State || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To ZIP Code</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="PostalCode"
            value={dataValues?.PostalCode || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Country</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Country"
            value={dataValues?.Country || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Phone</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Phone"
            value={dataValues?.Phone || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Fax</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Fax"
            value={dataValues?.Fax || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Email</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Email"
            value={dataValues?.Email || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
    </AddEditCatalogPopup>
  );
};

export default AddShipToPopup;
